export const DEFAULT_IMAGE_MODEL = 'gpt-image-2';

/**
 * Model values are sent to the backend as-is.
 * - 'auto' maps to the backend's current default.
 * - Other values can be either family names (e.g. 'z-image') or full model IDs (e.g. 'fal-ai/flux-2/edit').
 */
// Models for creating images from scratch (text-to-image)
export const IMAGE_MODEL_OPTIONS_GENERATE = [
  // Text-to-image families (backend: utils/generate_image.py)
  { value: 'gpt-image-2', label: 'GPT Image 2' },
  { value: 'z-image', label: 'Z-Image' },
  { value: 'reve', label: 'Reve' },
  { value: 'gpt-image-1', label: 'GPT-Image-1' },
  { value: 'nano-banana', label: 'Nano Banana' },
  { value: 'nano-banana-pro', label: 'Nano Banana Pro' },
  { value: 'nano-banana-2', label: 'Nano Banana 2' },
  { value: 'nano-banana-lite', label: 'Nano Banana Lite' },
  { value: 'seedream-5', label: 'Seedream 5' },
  { value: 'qwen-image-3', label: 'Qwen Image 3' },
  { value: 'grok-imagine-image-2', label: 'Grok Imagine Image 2.0' },
];

const COMMON_IMAGE_RATIOS = ['21:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16'];
const NANO_BANANA_RATIOS = ['21:9', '16:9', '3:2', '4:3', '5:4', '1:1', '4:5', '3:4', '2:3', '9:16'];
const GROK_IMAGINE_RATIOS = ['2:1', '20:9', '19.5:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16', '9:19.5', '9:20', '1:2'];

/**
 * Canvas-creator controls supported by each text-to-image family. These mirror
 * utils/generate_image.py; values are sent through the project asset endpoint.
 */
export const IMAGE_GENERATION_MODEL_SPECS = {
  'gpt-image-2': {
    supportsReferences: true,
    aspectRatios: COMMON_IMAGE_RATIOS,
    outputFormats: ['png', 'jpeg', 'webp'],
    qualityOptions: ['auto', 'low', 'medium', 'high'],
    defaultQuality: 'high',
  },
  'z-image': {
    supportsReferences: true,
    aspectRatios: [...COMMON_IMAGE_RATIOS, '9:21'],
    outputFormats: ['png', 'jpeg', 'webp'],
    supportsSeed: true,
    supportsSafetyChecker: true,
    supportsPromptExpansion: true,
    inferenceSteps: { min: 1, max: 30, default: 8 },
    accelerationOptions: ['none', 'regular', 'high'],
  },
  reve: {
    aspectRatios: ['16:9', '9:16', '3:2', '2:3', '4:3', '3:4', '1:1'],
    outputFormats: ['png', 'jpeg', 'webp'],
  },
  'gpt-image-1': {
    supportsReferences: true,
    aspectRatios: COMMON_IMAGE_RATIOS,
    outputFormats: ['png', 'jpeg', 'webp'],
  },
  'nano-banana': {
    supportsReferences: true,
    aspectRatios: NANO_BANANA_RATIOS,
    outputFormats: ['png', 'jpeg', 'webp'],
    resolutionOptions: ['1K', '2K', '4K'],
    supportsLimitGenerations: true,
    supportsWebSearch: true,
  },
  'nano-banana-pro': {
    supportsReferences: true,
    aspectRatios: NANO_BANANA_RATIOS,
    outputFormats: ['png', 'jpeg', 'webp'],
    resolutionOptions: ['1K', '2K', '4K'],
    supportsLimitGenerations: true,
    supportsWebSearch: true,
  },
  'nano-banana-2': {
    supportsReferences: true,
    aspectRatios: NANO_BANANA_RATIOS,
    outputFormats: ['png', 'jpeg', 'webp'],
    resolutionOptions: ['1K', '2K', '4K'],
    supportsLimitGenerations: true,
    supportsWebSearch: true,
  },
  'nano-banana-lite': {
    supportsReferences: true,
    aspectRatios: NANO_BANANA_RATIOS,
    outputFormats: ['png', 'jpeg', 'webp'],
    supportsLimitGenerations: true,
  },
  'seedream-5': {
    supportsReferences: true,
    aspectRatios: COMMON_IMAGE_RATIOS,
    supportsSafetyChecker: true,
  },
  'qwen-image-3': {
    supportsReferences: true,
    maxReferences: 3,
    aspectRatios: COMMON_IMAGE_RATIOS,
    outputFormats: ['png', 'jpeg', 'webp'],
    resolutionOptions: ['1K', '2K'],
    supportsSeed: true,
    supportsSafetyChecker: true,
    supportsPromptExpansion: true,
    defaultPromptExpansion: true,
  },
  'grok-imagine-image-2': {
    supportsReferences: true,
    maxReferences: 3,
    aspectRatios: GROK_IMAGINE_RATIOS,
    outputFormats: ['jpeg', 'png', 'webp'],
    resolutionOptions: ['1K', '2K'],
    qualityOptions: ['medium', 'low'],
    defaultQuality: 'medium',
  },
};

// Models for editing existing images (image-to-image / edit)
// Note: 'auto' is the default value but not shown as a selectable option.
// The ModelSelector handles displaying "Auto (default)" when value is 'auto'.
export const IMAGE_MODEL_OPTIONS_EDIT = [
  // Edit-capable aliases (backend: utils/generate_image_from_images.py)
  { value: 'gpt-image-2', label: 'GPT Image 2' },
  { value: 'nano-banana', label: 'Nano Banana' },
  { value: 'nano-banana-pro', label: 'Nano Banana Pro' },
  { value: 'nano-banana-2', label: 'Nano Banana 2' },
  { value: 'nano-banana-lite', label: 'Nano Banana Lite' },
  { value: 'flux-2', label: 'Flux 2' },
  { value: 'qwen-image-edit-plus', label: 'Qwen Image Edit Plus' },
  { value: 'gpt-image-1.5', label: 'GPT Image 1.5' },
  { value: 'seedream-5', label: 'Seedream 5' },
  { value: 'qwen-image-3', label: 'Qwen Image 3' },
  { value: 'grok-imagine-image-2', label: 'Grok Imagine Image 2.0' },
];

// Back-compat: treat IMAGE_MODEL_OPTIONS as the generation list by default.
export const IMAGE_MODEL_OPTIONS = IMAGE_MODEL_OPTIONS_GENERATE;
